import { useQuery, useQueries } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from './api-client';

// ─── Squares ──────────────────────────────────────────────────────────────────

export type SquaresMyPick = {
  squares_contest_id: string;
  pickem_id?: string;
  question_id?: string;
  contest_name_en?: string;
  contest_name_fr?: string;
  status: string;
  start_time_utc?: string;
  row_team_display_name?: string;
  col_team_display_name?: string;
  row_team_id?: string;
  col_team_id?: string;
  row_team_icon_url?: string;
  col_team_icon_url?: string;
  total_prize_amount: number;
  total_entries_to_date: number;
  entry_fee?: number;
  sport: string;
  total_winnings?: number;
  /** User reserved a seat but has not (yet) submitted picks (DKPLAT-533). */
  has_reservation?: boolean;
  /** User has submitted picks (a claimed square). Absent on legacy payloads. */
  has_selections?: boolean;
  /**
   * Live "squares are open for picks" signal, server-computed from the shared
   * reveal gate (see areQuestionsRevealed / square.controller.ts). Distinct from
   * the display-only `questions_posted_at` timestamp. Absent means unknown, which
   * a reserved card must treat as NOT available (DKPLAT-533).
   */
  has_questions?: boolean;
  questions_posted_at?: string | null;
  /**
   * Where this contest lives on the public web, absolute, from the backend. The card's link is
   * built on its origin — without it the lobby had to borrow a host from a squares contest that
   * happened to be listed in the lobby, and a finished contest is not listed.
   */
  public_url?: string | null;
  [key: string]: unknown;
};

export function useSquaresMyPicks() {
  return useQuery({
    queryKey: ['lobby-my-games', 'squares'],
    queryFn: async () => {
      const res = await apiClient.get<{ squares_contests: SquaresMyPick[] }>(
        '/api/squares/user/my-picks'
      );
      return res.data.squares_contests ?? [];
    },
    staleTime: 30_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

/**
 * Live / final scores for Squares contests on My Games.
 *
 * Same batch endpoint the Squares app's My Picks view uses. Polled while any fixture is live so
 * the Live tab's score row moves with the feed; settled contests keep their final scores without
 * further polling.
 */
export type SquaresFixtureScoreSnapshot = {
  externalId: string;
  status: 'scheduled' | 'live' | 'finished' | 'postponed' | 'cancelled';
  clock: string;
  quarter: number | null;
  homeScore: number | null;
  awayScore: number | null;
  homeTeam: { id: string; name: string } | null;
  awayTeam: { id: string; name: string } | null;
  scheduledDate: string;
};

export type SquaresFixtureScoresBatch = {
  success: boolean;
  fixtures: Record<string, SquaresFixtureScoreSnapshot | null>;
};

export function useSquaresFixtureScoresBatch(contestIds: string[]) {
  const stableIds = [...new Set(contestIds.filter(Boolean))].sort();

  return useQuery({
    queryKey: ['lobby-my-games', 'squares', 'fixture-scores', stableIds],
    queryFn: async () => {
      const res = await apiClient.get<SquaresFixtureScoresBatch>('/api/squares/fixture-scores', {
        params: { ids: stableIds.join(',') },
      });
      return res.data;
    },
    enabled: stableIds.length > 0,
    staleTime: 10_000,
    refetchInterval: query => {
      const fixtures = query.state.data?.fixtures ? Object.values(query.state.data.fixtures) : [];
      return fixtures.some(f => f?.status === 'live') ? 10_000 : false;
    },
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

// ─── Pickem ───────────────────────────────────────────────────────────────────

export type PickemItem = {
  pickem_id: string;
  title_en?: string;
  title_fr?: string;
  prize_text_en?: string;
  prize_text_fr?: string;
  status: string | number;
  start?: string;
  start_utc_timestamp?: number;
  entries?: number;
  has_entered?: boolean;
  /** User reserved a seat but has not (yet) submitted picks (DKPLAT-533). */
  has_reservation?: boolean;
  correct_picks?: number;
  total_picks?: number;
  total_points?: number;
  user_ranking?: number;
  sport?: string;
  league_logo_url?: string;
  entry_fee?: number;
  total_prize_amount?: number;
  open?: boolean;
  questions?: unknown[];
  /** Distinct underlying fixtures (games) the pickem covers — server-computed (DKPLAT-405). */
  games_count?: number;
  [key: string]: unknown;
};

export function usePickemPlay() {
  return useQuery({
    queryKey: ['lobby-my-games', 'pickem', 'play'],
    queryFn: async () => {
      const res = await apiClient.get<{ pickems: PickemItem[] }>('/api/pickems/play', {
        params: { selections: 'true' },
      });
      return res.data.pickems ?? [];
    },
    staleTime: 30_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

export function usePickemRecent() {
  return useQuery({
    queryKey: ['lobby-my-games', 'pickem', 'user-recent'],
    queryFn: async () => {
      const res = await apiClient.get<{ pickems: PickemItem[] }>('/api/pickems/user/recent', {
        params: { selections: 'true', rankings: 'true', limit: 50 },
      });
      return res.data.pickems ?? [];
    },
    staleTime: 30_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

// ─── Survivor ─────────────────────────────────────────────────────────────────

/**
 * Survivor has no "my games" endpoint of its own — nothing server-side answers "which
 * Survivor contests is this user in?". My Games therefore follows the Daily Reveal shape:
 * list the tenant's contests, then ask each one whether this user holds an entry.
 *
 * Types mirror `survivor.schema.ts` (hand-written, as the Survivor app's own hooks are).
 */

export type SurvivorContestSummary = {
  id: string;
  titleEn: string | null;
  sport: string;
  gameType: string;
  /** `pickems.pickem_status` — free-text varchar, so compare case-insensitively. */
  pickemStatus: string | null;
  startUtc: string | null;
  /**
   * The contest's own link, from the backend. Prefer it over deriving one from the lobby.
   *
   * Optional because a cached or older response will not carry it; the resolvers fall back to
   * the lobby-derived URL in that case.
   */
  publicUrl?: string | null;
};

export type SurvivorEntry = {
  id: string;
  /** `reserved` (seat held, no pick yet) | `active` | `eliminated`. */
  status: string;
  eliminatedWeekNumber: number | null;
  eliminationReason: string | null;
};

export type SurvivorPickSummary = {
  weekNumber: number;
  weekStatus: string;
  /** `survived` | `eliminated` | `void`, or null while the week is unsettled. */
  outcome: string | null;
};

export type SurvivorMyPicks = {
  /** Null when the user holds no entry in this contest — the "not my game" signal. */
  entry: SurvivorEntry | null;
  picks: SurvivorPickSummary[];
};

export type SurvivorWeekSummary = {
  weekNumber: number;
  /** `upcoming` | `open` | `locked` | `graded`. */
  status: string;
  opensAt: string | null;
  lockAt: string | null;
};

export type SurvivorContestDetail = {
  pickemId: string;
  pickem: {
    titleEn: string | null;
    sport: string | null;
    start: string | null;
    status: string | null;
  };
  contest: { prizingMode: string; jackpotPot: number; lastSurvivorPot: number };
  weeks: SurvivorWeekSummary[];
};

export type SurvivorStats = {
  total: number;
  survived: number;
  eliminated: number;
  survivedPct: number;
  eliminatedPct: number;
};

/** Tenant-wide contest list. Public, and the same endpoint the Survivor app boots from. */
export function useSurvivorContests() {
  return useQuery({
    queryKey: ['lobby-my-games', 'survivor', 'contests'],
    queryFn: async () => {
      const res = await apiClient.get<{ contests: SurvivorContestSummary[] }>('/api/survivor');
      return res.data.contests ?? [];
    },
    staleTime: 60_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

/**
 * One `/my-picks` per contest — this is what decides whether a contest belongs in My Games
 * at all, so it runs across every contest in the list.
 */
export function useSurvivorMyPicksBatch(pickemIds: string[]) {
  return useQueries({
    queries: pickemIds.map(id => ({
      queryKey: ['lobby-my-games', 'survivor', 'my-picks', id],
      queryFn: async () => {
        const res = await apiClient.get<SurvivorMyPicks>(`/api/survivor/${id}/my-picks`);
        return res.data;
      },
      enabled: !!id,
      staleTime: 30_000,
    })),
  });
}

/**
 * Contest config + week list, fetched ONLY for the contests the user turned out to be in.
 * It is what lets a card say which week is live and how far through the season the contest
 * is; running it across the whole tenant list would double the request count to describe
 * contests that are never rendered.
 */
export function useSurvivorContestsBatch(pickemIds: string[]) {
  return useQueries({
    queries: pickemIds.map(id => ({
      queryKey: ['lobby-my-games', 'survivor', 'contest', id],
      queryFn: async () => {
        const res = await apiClient.get<SurvivorContestDetail>(`/api/survivor/${id}`);
        return res.data;
      },
      enabled: !!id,
      staleTime: 60_000,
    })),
  });
}

/** Survived / eliminated split for My Games cards — same payload as the Survivor stat bar. */
export function useSurvivorStatsBatch(pickemIds: string[]) {
  return useQueries({
    queries: pickemIds.map(id => ({
      queryKey: ['lobby-my-games', 'survivor', 'stats', id],
      queryFn: async () => {
        const res = await apiClient.get<SurvivorStats>(`/api/survivor/${id}/stats`);
        return res.data;
      },
      enabled: !!id,
      staleTime: 30_000,
    })),
  });
}

// ─── Daily Reveal ─────────────────────────────────────────────────────────────

export type DRGame = {
  id: string;
  gameType: string;
  sport: string;
  pickemStatus: string | null;
  titleEn?: string | null;
};

export type DRCardDetails = {
  id: string;
  externalId: string;
  teamExternalId: string | null;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  position: string | null;
  teamName: string;
  teamAbbreviation: string | null;
  jerseyNumber: string | null;
  photoUrl: string | null;
  sport: string;
};

export type DRUserEntry = {
  status: 'revealed' | 'picked' | 'graded';
  isCorrect: boolean | null;
  totalHits: number;
  selectedCard: DRCardDetails | null;
  /** Frozen sportsbook line label, e.g. "86.5+ RUSH YDS" (prop-based contests). */
  propDisplayLabel?: string | null;
  propThreshold?: number | null;
  propOperator?: string | null;
  propResultStatus?: string | null;
  propResultValue?: number | null;
};

export type DRDaySummary = {
  id: string;
  dayNumber: number;
  gameDate: string;
  status: 'upcoming' | 'open' | 'locked' | 'graded';
  revealOpenAt: string | null;
  pickCutoffAt: string | null;
  userEntry: DRUserEntry | null;
};

export type DRGetGameResponse = {
  game: DRGame & {
    leagueIp?: boolean;
    textPrizeImageEn?: string | null;
    /** Per-contest overrides; `teamAssetFolder` picks the picks-core artwork folder. */
    styleConfig?: Record<string, unknown> | null;
  };
  days: DRDaySummary[];
  dailyHitThreshold?: number;
};

export type DRDayFixture = {
  id: string;
  externalFixtureId: string;
  homeTeam: string;
  homeTeamId: string | null;
  awayTeam: string;
  awayTeamId: string | null;
  startTime: string | null;
  status: 'scheduled' | 'live' | 'finished' | 'postponed' | 'cancelled';
  homeScore?: number;
  awayScore?: number;
};

export function useDailyRevealGames() {
  return useQuery({
    queryKey: ['lobby-my-games', 'daily-reveal', 'games'],
    queryFn: async () => {
      const res = await apiClient.get<{ games: DRGame[] }>('/api/daily-reveals');
      return res.data.games ?? [];
    },
    staleTime: 60_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

export function useDailyRevealGame(gameId: string | null) {
  return useQuery({
    queryKey: ['lobby-my-games', 'daily-reveal', 'game', gameId],
    queryFn: async () => {
      const res = await apiClient.get<DRGetGameResponse>(`/api/daily-reveals/${gameId}`);
      return res.data;
    },
    enabled: !!gameId,
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

export function useDailyRevealGamesBatch(gameIds: string[]) {
  return useQueries({
    queries: gameIds.map(id => ({
      queryKey: ['lobby-my-games', 'daily-reveal', 'game', id],
      queryFn: async () => {
        const res = await apiClient.get<DRGetGameResponse>(`/api/daily-reveals/${id}`);
        return res.data;
      },
      enabled: !!id,
      staleTime: 30_000,
    })),
  });
}

export function useDailyRevealFixtures(dayId: string | null) {
  return useQuery({
    queryKey: ['lobby-my-games', 'daily-reveal', 'fixtures', dayId],
    queryFn: async () => {
      const res = await apiClient.get<{ fixtures: DRDayFixture[] }>(
        `/api/daily-reveals/days/${dayId}/fixtures`
      );
      return res.data.fixtures ?? [];
    },
    enabled: !!dayId,
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

/**
 * Fixtures for every selected Daily Reveal day so My Games can bucket Upcoming /
 * Live / Recent off Sportradar status (same query key as `useDailyRevealFixtures`
 * so the per-card fetch dedupes).
 */
export function useDailyRevealFixturesBatch(dayIds: string[]) {
  return useQueries({
    queries: dayIds.map(id => ({
      queryKey: ['lobby-my-games', 'daily-reveal', 'fixtures', id],
      queryFn: async () => {
        const res = await apiClient.get<{ fixtures: DRDayFixture[] }>(
          `/api/daily-reveals/days/${id}/fixtures`
        );
        return res.data.fixtures ?? [];
      },
      enabled: !!id,
      staleTime: 15_000,
      refetchInterval: 30_000,
      retry: (count: number, err: unknown) => {
        if (axios.isAxiosError(err) && err.response?.status === 401) return false;
        return count < 2;
      },
    })),
  });
}

// ─── Game Grid (Bingo) ────────────────────────────────────────────────────────
//
// Like Survivor and Daily Reveal, Bingo serves no "my games" endpoint — nothing answers
// "which Game Grid contests is this user in?". So the same two-step shape: list the tenant's
// contests, then ask each one for this user's card. A card IS the entry, and the card endpoint
// answers 204 when none was dealt, which is exactly the membership test.

export type BingoContestSummary = {
  id: string;
  titleEn: string | null;
  sport: string;
  gameType: string;
  pickemStatus: string | null;
  startUtc: string | null;
  /**
   * Advertised pot from `GET /api/bingo`: sum of absolute cash pattern prizes when configured,
   * otherwise the legacy percentage-of-pool column. Optional because a contest merged in from
   * the lobby listing (direct-link / older cache) may not carry it — the card then falls back
   * to lobby prize copy.
   */
  prizePool?: number | null;
  /**
   * The contest's own link, from the backend. Prefer it over deriving one from the lobby.
   *
   * Optional because a cached or older response will not carry it; the resolvers fall back to
   * the lobby-derived URL in that case.
   */
  publicUrl?: string | null;
};

/**
 * What the card has won, evaluated server-side (`bingo.schema.ts` Outcome).
 *
 * `achieved` is every completed pattern kind; `payable` is what survives containment — an X
 * swallows the diagonals it is made of. A My Games card headlines the best PAYABLE tier,
 * because that is the one the contest actually pays on.
 */
export type BingoCardOutcome = {
  achieved: string[];
  payable: string[];
  payouts: Array<{
    patternKind: string;
    patternId: string;
    amount: number;
    prizeType: string;
    prizeValueText: string | null;
  }>;
};

/**
 * The player's card, trimmed to what a My Games card reads.
 *
 * `cells` carries far more (labels, odds, artwork) than a summary row needs, but the endpoint
 * returns the whole board and the counts below are derived from it — so it is typed loosely
 * here rather than duplicating the full Cell shape the Bingo app owns.
 */
export type BingoMyCard = {
  cardId: string;
  cardSize: number;
  freeCentre: boolean;
  dealtAt: string;
  committedAt: string | null;
  cells: Array<{ state: string; isFreeSpace: boolean; settledAt: string | null }>;
  outcome: BingoCardOutcome;
  spinsUsed: number;
};

/** Tenant-wide contest list. Public, and the same endpoint the Game Grid app boots from. */
export function useBingoContests() {
  return useQuery({
    queryKey: ['lobby-my-games', 'bingo', 'contests'],
    queryFn: async () => {
      const res = await apiClient.get<{ contests: BingoContestSummary[] }>('/api/bingo');
      return res.data.contests ?? [];
    },
    staleTime: 60_000,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) return false;
      return count < 2;
    },
  });
}

/**
 * One `/card` per contest — this is what decides whether a contest belongs in My Games at all.
 *
 * A 204 means "dealt no card", which is the whole membership test, and axios gives that back as
 * an empty body rather than an error. Normalised to `null` so a caller reads absence the same
 * way whichever shape the server used. A 401/404 is also `null` rather than a thrown error: a
 * signed-out visitor or a contest from another tenant simply has no card here, and letting
 * either reject would put the whole My Games tab into an error state over one contest.
 */
export function useBingoCardsBatch(pickemIds: string[]) {
  return useQueries({
    queries: pickemIds.map(id => ({
      queryKey: ['lobby-my-games', 'bingo', 'card', id],
      queryFn: async (): Promise<BingoMyCard | null> => {
        try {
          const res = await apiClient.get<BingoMyCard | ''>(`/api/bingo/${id}/card`);
          if (res.status === 204 || !res.data) return null;
          return res.data as BingoMyCard;
        } catch (err) {
          if (
            axios.isAxiosError(err) &&
            (err.response?.status === 401 || err.response?.status === 404)
          ) {
            return null;
          }
          throw err;
        }
      },
      enabled: !!id,
      // Cells settle while a game is on, so a live board's summary has to move with it — the
      // same cadence Daily Reveal's fixtures poll at.
      staleTime: 15_000,
      refetchInterval: 30_000,
      retry: (count: number, err: unknown) => {
        if (axios.isAxiosError(err) && err.response?.status === 401) return false;
        return count < 2;
      },
    })),
  });
}
